import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { flightStatusMap } from "../../../pages/Orders/OrdersData";
import { isGDSSupplier } from "../../../data/flight/supplier/getSupplierName";
import { getTestLevel } from "../../../utils/testLevel";
import { ActionContext } from "../OrderList";
import useAccount from "../../../hooks/useAccount";
import { useInsuranceActions } from "../../../hooks/useInsuranceActions";
import {
  CHANGE_ACTION,
  getChangeMessageCount,
  getChangeOverflowMenuActions,
} from "../../../data/flight/order/getChangeAdminGuidance";
import {
  VOID_ACTION,
  getVoidOverflowMenuActions,
} from "../../../data/flight/order/getVoidAdminGuidance";
import {
  REFUND_ACTION,
  getRefundOverflowMenuActions,
} from "../../../data/flight/order/getRefundAdminGuidance";

const POST_TICKETING_TABS = [
  "Tickets",
  "Void Orders",
  "Refund Orders",
  "Date Change",
  "Details Change",
  "Open Ticket",
];

const VIEW_REQUEST_PATH = {
  "Date Change": (id) => `/order/flight/ticket/changedate/confirmation/${id}`,
  "Details Change": (id) => `/order/flight/ticket/rename/confirmation/${id}`,
  "Open Ticket": (id) =>
    `/order/flight/ticket/changedate/confirmation/${id}?page=Open Ticket`,
  "Refund Orders": (id) => `/order/flight/ticket/refund/confirmation/${id}`,
  "Void Orders": (id) => `/order/flight/ticket/void/confirmation/${id}`,
};

function ticketQueueId(row) {
  const queue = row?.ticketQueue;
  const ticket = Array.isArray(queue) ? queue.find(Boolean) : queue;
  if (!ticket) return "";
  if (typeof ticket === "string") return ticket;
  return ticket._id || ticket.id || "";
}

/** Messages on an order are only readable from the ticket. Bookings offer
 * the thread once a ticket is in the queue. A ticket row is that ticket. */
function orderMessageTarget(og, bookingData, ticketFilterType) {
  const flight = og?.flightObj || bookingData;
  const flightBookingId = flight?._id || "";
  if (!flightBookingId) return null;
  const ticketId =
    ticketFilterType === "Tickets" ? og?._id : ticketQueueId(og);
  if (!ticketId) return null;
  return {
    ticketId,
    flightBookingId,
    orderId: og?.bookingId || flight?.bookingId || og?.orderId || "",
    flightBooking: flight,
  };
}

function pushOverflowActions(menus, actions, handlers) {
  menus.push(
    ...actions.map((action) => ({
      label: action.label,
      action: handlers[action.key],
      hide: !handlers[action.key],
    })),
  );
}

function getVoidMenuHandlers(value, og, navigate) {
  return {
    [VOID_ACTION.review]: () => value?.reviewVoid?.(og),
    [VOID_ACTION.voidTicket]: () => value?.submitVoid?.(og),
    [VOID_ACTION.manualVoid]: () => value?.recordManualVoid?.(og),
    [VOID_ACTION.assign]: () => value?.assignVoid?.(og),
    [VOID_ACTION.decline]: () => value?.denyVoid?.(og),
    [VOID_ACTION.airlineDeclined]: () =>
      value?.promptAirlineDeclinedVoid
        ? value.promptAirlineDeclinedVoid(og)
        : value?.denyVoid?.(og),
    [VOID_ACTION.remark]: () =>
      value?.addVoidRemark
        ? value.addVoidRemark(og)
        : navigate(`/order/flight/ticket/void/confirmation/${og?._id}`),
    [VOID_ACTION.messages]: () =>
      value?.setOpenVoidMessages
        ? value.setOpenVoidMessages(og)
        : value?.checkVoidMessages?.(og),
    [VOID_ACTION.supplierResponse]: () =>
      value?.viewVoidSupplierResponse?.(og),
  };
}

function getRefundMenuHandlers(value, og, navigate) {
  return {
    [REFUND_ACTION.review]: () => value?.reviewRefund?.(og),
    [REFUND_ACTION.autoQuote]: () => value?.refreshRefundFee?.(og),
    [REFUND_ACTION.quote]: () => value?.setOpenRefundQuote?.(og),
    [REFUND_ACTION.approveQuote]: () => value?.approveRefundQuote?.(og),
    [REFUND_ACTION.submit]: () => value?.setOpenSubmitRefund?.(og?._id),
    [REFUND_ACTION.retryAuto]: () =>
      value?.promptRetryAutoRefund
        ? value.promptRetryAutoRefund(og)
        : value?.setOpenSubmitRefund?.(og?._id),
    [REFUND_ACTION.checkStatus]: () => value?.refreshRefund?.(og),
    [REFUND_ACTION.reimburse]: () => value?.setOpenManualRefund?.(og),
    [REFUND_ACTION.assign]: () => value?.refundTicket?.(og),
    [REFUND_ACTION.decline]: () => value?.denyRefund?.(og),
    [REFUND_ACTION.remark]: () =>
      value?.addRefundRemark
        ? value.addRefundRemark(og)
        : navigate(`/order/flight/ticket/refund/confirmation/${og?._id}`),
    [REFUND_ACTION.messages]: () =>
      value?.setOpenRefundMessages
        ? value.setOpenRefundMessages(og)
        : value?.checkRefundMessages?.(og),
    [REFUND_ACTION.supplierResponse]: () =>
      value?.viewRefundSupplierResponse
        ? value.viewRefundSupplierResponse(og)
        : value?.viewVoidSupplierResponse?.(og),
  };
}

function getChangeMenuHandlers(value, og, ticketFilterType) {
  const isOpenTicketTab = ticketFilterType === "Open Ticket";
  const isDetailsTab = ticketFilterType === "Details Change";
  const useManualQuote =
    isDetailsTab ||
    og?.changeRequest === "PassengerInfo" ||
    Boolean(og?.newDirections);
  const reviewOpen = () =>
    value?.reviewOpenTicket
      ? value.reviewOpenTicket(og)
      : value?.reviewChange?.(og);

  return {
    [CHANGE_ACTION.review]: () =>
      isOpenTicketTab ? reviewOpen() : value?.reviewChange?.(og),
    [CHANGE_ACTION.accept]: () => value?.reviewChange?.(og),
    [CHANGE_ACTION.startOpen]: reviewOpen,
    [CHANGE_ACTION.proceedOpen]: () => value?.processOpenTicket?.(og),
    [CHANGE_ACTION.notEligible]: () =>
      value?.processOpenTicket
        ? value.processOpenTicket(og)
        : value?.reviewOpenTicket?.(og),
    [CHANGE_ACTION.recordOpened]: () => value?.recordOpenTicket?.(og),
    [CHANGE_ACTION.airlineDeclined]: () =>
      isOpenTicketTab
        ? value?.recordOpenTicket?.(og)
        : value?.recordChangeSupplierOutcome?.({
            ...og,
            outcomeKind: "supplier_decline",
          }),
    [CHANGE_ACTION.processingFailed]: () =>
      isOpenTicketTab
        ? value?.recordOpenTicket?.(og)
        : value?.recordChangeSupplierOutcome?.({
            ...og,
            outcomeKind: "technical_failure",
          }),
    [CHANGE_ACTION.needMoreInfo]: () => value?.recordOpenTicket?.(og),
    [CHANGE_ACTION.quote]: () =>
      useManualQuote
        ? value?.setOpenManualChangeQuote?.(og)
        : value?.setOpenChangeQuote?.(og),
    [CHANGE_ACTION.adminPay]: () => value?.setOpenAdminChangePay?.(og),
    [CHANGE_ACTION.reviewBankProof]: () =>
      value?.setOpenChangeBankTransferProof?.(og),
    [CHANGE_ACTION.submit]: () =>
      value?.promptSubmitChange
        ? value.promptSubmitChange(og)
        : value?.setOpenSubmitChange?.(og?._id),
    [CHANGE_ACTION.manualChange]: () =>
      value?.recordManualChange
        ? value.recordManualChange(og)
        : value?.setOpenManualUpdate?.(og),
    [CHANGE_ACTION.confirmReissue]: () =>
      value?.promptConfirmReissue
        ? value.promptConfirmReissue(og)
        : value?.setOpenManualUpdate?.(og),
    [CHANGE_ACTION.gdsReissue]: () => value?.assignChangeTask?.(og),
    [CHANGE_ACTION.retryAuto]: () =>
      value?.promptRetryAutoChange
        ? value.promptRetryAutoChange(og)
        : value?.promptSubmitChange?.(og),
    [CHANGE_ACTION.assign]: () => value?.assignChangeTask?.(og),
    [CHANGE_ACTION.decline]: () => value?.denyChange?.(og),
    [CHANGE_ACTION.remark]: () => value?.setOpenUpdateChangeRequest?.(og),
    [CHANGE_ACTION.fareRules]: () => value?.setOpenChangeFareRules?.(og),
    [CHANGE_ACTION.messages]: () => value?.setOpenChangeMessages?.(og),
    [CHANGE_ACTION.viewSelectedOffer]: () =>
      value?.setOpenChangeSelectedOffer?.(og),
    [CHANGE_ACTION.supplierResponse]: () =>
      value?.viewChangeSupplierResponse
        ? value.viewChangeSupplierResponse(og)
        : value?.viewVoidSupplierResponse?.(og),
  };
}

function getTicketMenuItems(value, og, bookingData, checkPermission) {
  return [
    {
      label: "Issue Ticket",
      action: () => value?.issue?.(og?._id),
      showFor: ["Pending", "RequestedInfo"],
      hide: !checkPermission("issue:ticket"),
    },
    {
      label: "Manual Issue",
      action: () => value?.manualIssue?.(og),
      showFor: ["Pending", "WaitingConfirmation"],
    },
    {
      label: "Cancel Ticket",
      action: () => value?.setOpenCancelTicket?.(bookingData),
      showFor: ["Ticket issued", "issued", "Completed"],
    },
    {
      label: "Approve",
      action: () => value?.approve?.(og?._id),
      showFor: ["Open"],
    },
    {
      label: "Cancel Request",
      action: () => value?.deny?.(og),
    },
    {
      label: "Request more info",
      action: () => value?.requestInfo?.(og),
      showFor: ["Open"],
    },
  ];
}

function getBookingMenuItems({
  value,
  og,
  bookingData,
  checkPermission,
  navigate,
  voidTime,
  handleAddInsurance,
}) {
  const isDev = getTestLevel() === getTestLevel("dev");
  return [
    {
      label: "Pay from agent wallet",
      action: () => value?.setOpenPayment?.(og?.flightObj?._id),
      showFor: ["Not Paid", "booked"],
      hide: !checkPermission("pay:ticket"),
    },
    {
      label: "Issue Ticket",
      action: () => value?.setOpenIssueTicket?.(bookingData),
      showFor: ["Paid", "issuable"],
      hide: !checkPermission("issue:ticket"),
    },
    {
      label: "Hold booking",
      action: () => value?.holdFlightBooking?.(og?.flightObj?._id),
      showFor: ["Not Paid", "booked"],
      hide: !checkPermission("hold:flight-booking"),
    },
    {
      label: "Edit Payable",
      action: () => value?.setOpenEditPayable?.(og),
      showFor: ["Not Paid", "booked"],
      hide: !checkPermission("hold:flight-booking"),
    },
    {
      label: "Add a bag",
      action: () => value?.addBag?.(og),
      showFor: [
        "Not Paid",
        "booked",
        "Ticket issued",
        "issued",
        "Paid",
        "issuable",
      ],
      hide: !isDev,
    },
    {
      label: "Add a seat",
      action: () => value?.addSeat?.(og),
      showFor: [
        "Not Paid",
        "booked",
        "Ticket issued",
        "issued",
        "Paid",
        "issuable",
      ],
    },
    {
      label: "Add hotel",
      action: () => {},
      showFor: ["Not Paid", "booked", "Paid", "issuable"],
      hide: !isDev,
    },
    {
      label: "Add tour",
      action: () => {},
      showFor: ["Not Paid", "booked", "Paid", "issuable"],
      hide: !isDev,
    },
    {
      label: "Add protection",
      action: handleAddInsurance,
      showFor: ["Not Paid", "booked", "Paid", "issuable"],
      hide: !isDev,
    },
    {
      label: "Rebook",
      action: () => value?.setOpenRebook?.(og?.flightObj),
    },
    {
      label: "Change date",
      action: () => navigate(`/order/flight/ticket/changedate/${og?._id}`),
      showFor: ["Ticket issued", "issued"],
    },
    {
      label: "Details Change",
      action: () => navigate(`/order/flight/ticket/changeinfo/${og?._id}`),
      showFor: ["Ticket issued", "issued"],
    },
    {
      label: "Refund",
      action: () => navigate(`/order/flight/ticket/refund/${og?._id}`),
      showFor: ["Ticket issued", "issued"],
      hide: og?.status === "Approved",
    },
    {
      label: "Download pdf",
      action: () => value?.setOpenPDFExport?.(og?._id),
      hide: !isDev,
    },
    {
      label: "Share order",
      action: () => value?.setOpenEmailExport?.(og?._id),
      hide: !isDev,
    },
    {
      label: "Share pnr",
      action: () => {},
      hide: !isGDSSupplier(bookingData?.supplier) || !isDev,
    },
    {
      label: "Cancel order",
      action: () => value?.setOpenCancelOrder?.(og?.flightObj?._id),
      showFor: ["Not Paid", "booked", "Paid", "issuable"],
      hide: !checkPermission("cancel:flight-booking"),
    },
    {
      label: "Void",
      action: () => navigate(`/order/flight/ticket/void/${og?._id}`),
      hide: voidTime - new Date() <= 0,
      showFor: ["Ticket issued", "issued", "Denied"],
    },
  ];
}

function buildMenus({
  value,
  og,
  bookingData,
  ticketFilterType,
  isDefaultOrderMenu,
  isPriceFreezeOrder,
  isInitiatedPriceFreeze,
  postTicketing,
  checkPermission,
  navigate,
  voidTime,
  handleAddInsurance,
}) {
  const menus = [
    {
      label: postTicketing ? "View Request" : "View order",
      action: () => {
        const path = VIEW_REQUEST_PATH[ticketFilterType];
        if (path) navigate(path(og?._id));
        else
          value?.setOpenViewDetail?.({ id: og?._id, page: ticketFilterType });
      },
    },
  ];

  if (isInitiatedPriceFreeze && isDefaultOrderMenu) {
    menus.push(
      {
        label: "Manage freeze",
        action: () => value?.setOpenManageFreeze?.(og),
      },
      {
        label: "Approve freeze",
        action: () =>
          value?.setOpenApproveFreeze?.({
            priceFreezeId: bookingData?.priceFreezeId,
            status: "APPROVED",
          }),
      },
      {
        label: "Decline freeze",
        action: () =>
          value?.setOpenApproveFreeze?.({
            priceFreezeId: bookingData?.priceFreezeId,
            status: "DECLINED",
          }),
      },
      {
        label: "Cancel order",
        action: () => value?.setOpenCancelOrder?.(og?.flightObj?._id),
        hide: !checkPermission("cancel:flight-booking"),
      },
    );
    return menus;
  }

  if (isPriceFreezeOrder && isDefaultOrderMenu) {
    menus.push({
      label: "Manage freeze",
      action: () => value?.setOpenManageFreeze?.(og),
    });
  }

  const messageTarget = orderMessageTarget(og, bookingData, ticketFilterType);
  if (
    messageTarget &&
    (ticketFilterType === "Bookings" || ticketFilterType === "Tickets")
  ) {
    const count = getChangeMessageCount(messageTarget.flightBooking);
    menus.push({
      label: count > 0 ? `Check messages (${count})` : "Check messages",
      action: () => value?.setOpenOrderMessages?.(messageTarget),
    });
  }

  menus.push({
    label: "Refresh Status",
    action: () => value?.refreshTicket?.(bookingData?._id),
    showFor: ["Completed", "PendingVoid", "WaitingConfirmation"],
  });

  if (ticketFilterType === "Tickets") {
    menus.push(...getTicketMenuItems(value, og, bookingData, checkPermission));
  } else if (ticketFilterType === "Void Orders") {
    pushOverflowActions(
      menus,
      getVoidOverflowMenuActions(og),
      getVoidMenuHandlers(value, og, navigate),
    );
  } else if (ticketFilterType === "Refund Orders") {
    pushOverflowActions(
      menus,
      getRefundOverflowMenuActions(og),
      getRefundMenuHandlers(value, og, navigate),
    );
  } else if (
    ticketFilterType === "Date Change" ||
    ticketFilterType === "Details Change" ||
    ticketFilterType === "Open Ticket"
  ) {
    pushOverflowActions(
      menus,
      getChangeOverflowMenuActions(og),
      getChangeMenuHandlers(value, og, ticketFilterType),
    );
  }

  if (!postTicketing) {
    menus.push(
      ...getBookingMenuItems({
        value,
        og,
        bookingData,
        checkPermission,
        navigate,
        voidTime,
        handleAddInsurance,
      }),
    );
  }

  return menus;
}

export default function FlightMenu({ og, mobile }) {
  const { searchForInsuranceFromFlightBooking } = useInsuranceActions();
  const bookingData = Array.isArray(og?.flightBooking)
    ? og.flightBooking.at(0)
    : og?.flightBooking;
  const isPriceFreezeOrder = Boolean(bookingData?.priceFreezeId);
  const isInitiatedPriceFreeze =
    isPriceFreezeOrder && bookingData?.priceFreezeStatus === "Initiated";
  const { ticketFilterType } = useSelector((state) => state.order);
  const { checkPermission } = useAccount();
  const voidTime = new Date(bookingData?.voidDeadline || null);
  const navigate = useNavigate();

  const handleAddInsurance = () => {
    searchForInsuranceFromFlightBooking({ data: og });
  };

  return (
    <div
      className={`${
        mobile
          ? "flex flex-col z-10 w-full"
          : "absolute p-2 flex flex-col z-10 bg-white rounded-md right-2 top-full shadow-xl min-w-[200px]"
      } `}
      style={{
        boxShadow: "0 5px 15px #999",
      }}
    >
      <ActionContext.Consumer>
        {(value) => {
          const postTicketing =
            og?.fee !== undefined || og?.ticketNo || og?.changeRequest;
          const isDefaultOrderMenu =
            !postTicketing && !POST_TICKETING_TABS.includes(ticketFilterType);

          const menus = buildMenus({
            value,
            og,
            bookingData,
            ticketFilterType,
            isDefaultOrderMenu,
            isPriceFreezeOrder,
            isInitiatedPriceFreeze,
            postTicketing,
            checkPermission,
            navigate,
            voidTime,
            handleAddInsurance,
          });

          const status = flightStatusMap(og?.status || bookingData?.status);
          const items = menus.filter(
            (obj) =>
              (obj?.showFor ? obj.showFor.includes(status) : true) &&
              !obj?.hide,
          );

          return items.map((item, index) => (
            <div
              key={index}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                // Defer so the same click cannot hit a portal overlay and close it.
                window.setTimeout(() => item?.action?.(), 0);
              }}
              className={`font-bold text-sm px-2 py-2 hover:bg-[#818083]/10 border-b ${
                item?.danger ? "text-red-600" : "text-[#444]"
              }`}
            >
              {item?.label}
            </div>
          ));
        }}
      </ActionContext.Consumer>
    </div>
  );
}
